import { http } from '@/lib/http';
import type {
  SalesOrder,
  SalesOrderPaymentMode,
  SalesOrderStatus,
} from '@/types/orders';

const BASE = '/api/v1/orders';

export interface OrderListQuery {
  status?: SalesOrderStatus;
  customerId?: string;
  externalOrderRef?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface OrderListResponse {
  rows: SalesOrder[];
  page: number;
  pageSize: number;
  total: number;
}

export interface OrderLinePayload {
  itemId: string;
  /** Always in BASE units (FE converts before sending if alternate UoM was picked). */
  quantity: number;
  /** Optional override; absent means "no per-line price" (BE leaves lineTotal null). */
  unitPrice?: number;
}

/**
 * M-money-model — order-level money fields. Embedded into Create and
 * sent stand-alone via PATCH /orders/:id/totals on the order detail.
 */
export interface OrderMoneyPayload {
  shippingCharge?: number;
  discountAmount?: number;
  additionalCharges?: Array<{ label: string; amount: number }>;
  roundOff?: number;
}

export interface CreateOrderPayload {
  customerId: string;
  warehouseId: string;
  items: OrderLinePayload[];
  externalOrderRef?: string;
  notes?: string;
  /** Reserve stock immediately on create (default true on BE). */
  reserveStock?: boolean;
  /**
   * Optional ISO 4217 currency. When absent, BE picks
   * Customer.defaultCurrency then Tenant.defaultCurrency.
   */
  currency?: string;
  /**
   * D-5 — defaults to PREPAID. COD orders skip the payment-confirm
   * gate; the dispatch automation fires when stock reserves and the
   * partner collects the order total on delivery.
   */
  paymentMode?: SalesOrderPaymentMode;
  /** M-money-model — shipping / discount / additional / round-off. */
  money?: OrderMoneyPayload;
}

export interface UpdateOrderStatusPayload {
  status: SalesOrderStatus;
  reason?: string;
}

export const ordersService = {
  list: (query: OrderListQuery = {}) =>
    http.get<OrderListResponse>(BASE, { params: query }),
  get: (id: string) => http.get<SalesOrder>(`${BASE}/${id}`),
  create: (payload: CreateOrderPayload) =>
    http.post<SalesOrder, CreateOrderPayload>(BASE, payload),
  updateStatus: (id: string, payload: UpdateOrderStatusPayload) =>
    http.patch<SalesOrder, UpdateOrderStatusPayload>(`${BASE}/${id}/status`, payload),
  /** M-money-model — edit shipping / discount / charges / round-off. */
  updateTotals: (id: string, payload: OrderMoneyPayload) =>
    http.patch<SalesOrder, OrderMoneyPayload>(`${BASE}/${id}/totals`, payload),
};
